<template>
	<div class="stack">
		<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
			<span
				>Create a new API key to allow your applications to interact with the Signaloid Cloud Compute Engine
				API.</span
			>
		</div>
		<section class="generate-key-form stack">
			<div class="input-group">
				<div class="field-label">Key name (optional)</div>
				<v-text-field
					v-model="keyName"
					maxlength="500"
					placeholder="e.g., Production Server Key"
					hide-details
					data-test="generate-key-name-input"
				/>
			</div>
			<div class="input-group">
				<div class="field-label">Validity period</div>
				<div class="sgnl-flex sgnl-flex-column sgnl-flex-sm-row sgnl-gap-8">
					<v-select
						v-model="validitySelection"
						:items="validityDateOptions"
						hide-details
						:menu-icon="''"
						data-test="generate-key-validity-period-selector"
						:class="{ 'sgnl-flex-1': validitySelection === 'Custom...' }"
					>
						<template #append-inner>
							<ChevronDown16 class="select-chevron" />
						</template>
					</v-select>
					<SignaloidDatePicker
						v-if="validitySelection === 'Custom...'"
						v-model:date="signaloidDatePickerPickedDate"
						hide-details
						readonly
						:allowed-dates="datePickerAllowedDates"
						class="sgnl-flex-1"
					/>
				</div>
			</div>
			<div
				class="sgnl-paragraph-s-regular sgnl-text-color-secondary"
				data-test="generate-key-expiration-description"
			>
				<span v-if="validitySelection !== 'Valid forever'">
					The key will expire on {{ expirationDateFormatted }}.
				</span>
				<span v-else> The key will never expire. </span>
			</div>
			<LimitableActionButton
				variant="flat"
				color="primary"
				data-test="generate-key-button"
				:disabled="generateKeyButtonDisabled"
				:loading="generateKeyButtonLoading"
				:limits-to-check="[UserLimitsE.KeyCount]"
				@valid-click="generateKey"
			>
				<template #btnContent><Add16 /> <span>Generate API Key</span></template>
			</LimitableActionButton>
			<AlertMessage
				v-if="errorText !== ''"
				type="error"
				>{{ errorText }}</AlertMessage
			>
		</section>
		<APIKeyCreationDialog
			v-if="showKeyCreationDialog"
			v-model:showDialog="showKeyCreationDialog"
			:passed-name="showKeyCreationDialogConfig?.name"
			:passed-key="showKeyCreationDialogConfig?.key"
		/>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Add16 from "@carbon/icons-vue/es/add/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";

// Types
import { AWSAuthenticatedUserObject } from "@/types/user";
import { UserLimitsE } from "@/js/tierconfig";

// Libraries
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { toISODateString, isFutureDateString } from "@/js/dateUtils";

// Utilities
import * as Sentry from "@sentry/vue";

// Components
import SignaloidDatePicker from "@/components/Common/SignaloidDatePicker.vue";
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import AlertMessage from "@/components/Common/AlertMessage.vue";
import APIKeyCreationDialog from "./APIKeyCreationDialog.vue";

// Stores
import { useUserStore } from "@/stores/user";
import { useRootStore } from "@/stores/root";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";

type ComponentData = {
	keyName: string | undefined;
	generateKeyButtonLoading: boolean;
	authenticatedUser: undefined | null | AWSAuthenticatedUserObject;
	validityDateOptions: ValidUntilOption[];
	validitySelection: ValidUntilOption;
	signaloidDatePickerPickedDate: string;
	showKeyCreationDialog: boolean;
	errorText: string;
	showKeyCreationDialogConfig: {
		key: string;
		name: string;
	} | null;
};

type ValidUntilOption = "7 days" | "30 days" | "60 days" | "90 days" | "One year" | "Custom..." | "Valid forever";

const k7Days: ValidUntilOption = "7 days";
const k30Days: ValidUntilOption = "30 days";
const k60Days: ValidUntilOption = "60 days";
const k90Days: ValidUntilOption = "90 days";
const kOneYear: ValidUntilOption = "One year";
const kCustom: ValidUntilOption = "Custom...";
const kValidForever: ValidUntilOption = "Valid forever";

const kValidityDateOptionList: ValidUntilOption[] = [
	k7Days,
	k30Days,
	k60Days,
	k90Days,
	kOneYear,
	kCustom,
	kValidForever,
];

export default defineComponent({
	name: "GenerateAPIKey",
	props: {},
	emits: ["keyCreated"],
	components: {
		SignaloidDatePicker,
		LimitableActionButton,
		AlertMessage,
		APIKeyCreationDialog,
		Add16,
		ChevronDown16,
	},
	data: (): ComponentData => ({
		signaloidDatePickerPickedDate: toISODateString(new Date()),
		keyName: undefined,
		generateKeyButtonLoading: false,
		validitySelection: "30 days",
		authenticatedUser: undefined,
		validityDateOptions: kValidityDateOptionList,
		showKeyCreationDialog: false,
		showKeyCreationDialogConfig: null,
		errorText: "",
	}),
	computed: {
		expirationDateISO(): string | null {
			const validUntilDate = new Date(); // Init value before applying selection
			switch (this.validitySelection) {
				case k7Days:
					validUntilDate.setDate(validUntilDate.getDate() + 7);
					return toISODateString(validUntilDate);
				case k30Days:
					validUntilDate.setDate(validUntilDate.getDate() + 30);
					return toISODateString(validUntilDate);
				case k60Days:
					validUntilDate.setDate(validUntilDate.getDate() + 60);
					return toISODateString(validUntilDate);
				case k90Days:
					validUntilDate.setDate(validUntilDate.getDate() + 90);
					return toISODateString(validUntilDate);
				case kOneYear:
					validUntilDate.setFullYear(validUntilDate.getFullYear() + 1);
					return toISODateString(validUntilDate);
				case kCustom: {
					const pickedDateParsed = new Date(this.signaloidDatePickerPickedDate);
					return toISODateString(pickedDateParsed);
				}
				case kValidForever:
					return null;

				default:
					throw new Error("Unhandled this.validitySelection:" + this.validitySelection);
			}
		},
		expirationDateFormatted(): string {
			if (this.expirationDateISO) {
				// Transform from ISO to local
				const date = new Date(this.expirationDateISO);
				return date.toLocaleDateString();
			}
			return String(this.expirationDateISO);
		},
		generateKeyButtonDisabled(): boolean {
			const dateNever = this.expirationDateISO === null && this.validitySelection === "Valid forever";
			const dateValid = this.expirationDateISO !== null && this.datePickerAllowedDates(this.expirationDateISO);
			return !(dateNever || dateValid);
		},
	},
	setup() {
		const userStore = useUserStore();
		const rootStore = useRootStore();
		return { userStore, rootStore, UserLimitsE };
	},
	methods: {
		datePickerAllowedDates: (val: string) => {
			return isFutureDateString(val);
		},
		async generateKey() {
			this.generateKeyButtonLoading = true;
			this.errorText = "";

			try {
				const defaultName = "Signaloid Cloud Key";
				const name = this.keyName ?? defaultName;

				let validUntil: number | null = null;
				if (this.expirationDateISO === null) {
					// "Valid forever" was selected — send null so the key never expires
					validUntil = null;
				} else if (this.expirationDateISO !== undefined) {
					validUntil = Math.floor(Date.parse(this.expirationDateISO) / 1000);
				} else {
					// No validity period selected — default to 30 days from now
					const defaultDate = new Date();
					defaultDate.setDate(defaultDate.getDate() + 30);
					validUntil = Math.floor(defaultDate.getTime() / 1000);
				}

				const response = await sdk.keys.create({ Name: name, ValidUntil: validUntil });
				if (!response.Key) {
					throw new Error("API key creation response did not include the key value.");
				}
				this.showKeyCreationDialogConfig = {
					key: response.Key,
					name: response.Name,
				};

				// @ts-ignore
				this.$posthog?.capture("api_key_created");

				this.$emit("keyCreated");
				this.showKeyCreationDialog = true;
				this.keyName = undefined;
				// this.signaloidDatePickerPickedDate = undefined;
			} catch (error) {
				// TODO: Add monitoring
				let severityLevel: Sentry.SeverityLevel = "error";
				let isTierLimitError = false;
				if (error instanceof SdkError) {
					/*
					 *	This API can only return: 401, 404 (UserID), 500, 403
					 */
					switch (error.code) {
						case ERROR_CODES.API_SERVER_ERROR:
							severityLevel = "fatal";
							this.errorText =
								"Our system encountered a server error while creating the API key." +
								" If this error persists, contact developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_UNAUTHORIZED:
							this.errorText =
								"Our system encountered an authorization error while creating the API key." +
								" Signing out and back in will ensure you are properly authorized.";
							break;
						case ERROR_CODES.API_NOT_FOUND:
							severityLevel = "fatal";
							this.errorText =
								"Our system encountered an error while creating the API key." +
								" Our team will receive a high priority notification for this issue." +
								" If this error persists, contact developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_FORBIDDEN:
							// 403 = the tier's API key allowance is used up: expected, not a bug
							isTierLimitError = true;
							this.errorText = "You have reached the API key limit for your account.";
							this.rootStore.tierLimitEventBus.emit({
								type: TierLimitEventTypeE.LimitExceeded,
								affectedLimits: [UserLimitsE.KeyCount],
							});
							break;

						default:
							this.errorText =
								`Our system encountered an unexpected error (${error.code}) while creating the API key.` +
								" If this error persists, contact developer-support@signaloid.com.";
							break;
					}
				} else {
					// Network / other error (no response)
					this.errorText =
						"We could not reach our servers. Often this is a temporary network or connectivity issue.";
				}
				console.log(error);
				if (!isTierLimitError) {
					Sentry.captureException(error, {
						level: severityLevel,
						extra: { userMessage: this.errorText },
						tags: { service: "create-api-key" },
					});
				}
				// TODO: error handling
				//  Can fail with: 401, 403, 400, 500
			} finally {
				this.generateKeyButtonLoading = false;
			}

			//TODO:
			// Post to /keys
			// Show new key (only once!)
		},
	},
	async created() {
		const authUser = await this.userStore.getCurrentAuthenticatedUserObject();
		this.authenticatedUser = authUser;

		// Set default date picker picked date to +30 days.
		const datePickerPickedDate = new Date(this.signaloidDatePickerPickedDate);
		datePickerPickedDate.setDate(datePickerPickedDate.getDate() + 30);
		this.signaloidDatePickerPickedDate = toISODateString(datePickerPickedDate);
	},
});
</script>

<style lang="scss" scoped>
.generate-key-form {
	max-width: var(--sgnl-container-343);
}
</style>
